import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { apiFetch } from "../lib/api.js";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = loading, null = signed out
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const me = await apiFetch("/auth/me");
      setUser(me);
    } catch (e) {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    setError(null);
    try {
      const me = await apiFetch("/auth/login", { method: "POST", body: { email, password } });
      setUser(me);
      return true;
    } catch (e) {
      setError(e.message);
      return false;
    }
  }, []);

  const signup = useCallback(async (email, password, inviteCode) => {
    setError(null);
    try {
      const me = await apiFetch("/auth/signup", { method: "POST", body: { email, password, inviteCode } });
      setUser(me);
      return true;
    } catch (e) {
      setError(e.message);
      return false;
    }
  }, []);

  const logout = useCallback(async () => {
    await apiFetch("/auth/logout", { method: "POST" });
    setUser(null);
  }, []);

  const requestPasswordReset = useCallback(async (email) => {
    setError(null);
    try {
      const res = await apiFetch("/auth/forgot-password", { method: "POST", body: { email } });
      return res;
    } catch (e) {
      setError(e.message);
      return null;
    }
  }, []);

  const resetPassword = useCallback(async (token, password) => {
    setError(null);
    try {
      const res = await apiFetch("/auth/reset-password", { method: "POST", body: { token, password } });
      return res;
    } catch (e) {
      setError(e.message);
      return null;
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, error, login, signup, logout, requestPasswordReset, resetPassword }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
